/**
 * IvtLayout — chrome shell for the entire /ivt/* tree.
 *
 * Decoupled from the portal's main chrome:
 *   - No portal Sidebar — IVT is its own product surface
 *   - The right-side ChatPanel is NOT owned here. It's hoisted into
 *     <ChatShell /> (App.tsx) so the same ChatPanel fiber (and its
 *     session/messages/streaming state) is shared with the main app.
 *
 * Sub-routes are mounted via <Outlet /> from the parent App.tsx route.
 */

import { NavLink, Outlet } from 'react-router-dom';
import { useAuth } from '../../../context/AuthContext';
import ImpersonationBanner from '../../../components/ImpersonationBanner/ImpersonationBanner';
import {
  hasIvtAccess,
  hasIvtAdminAccess,
  IVT_ACCESS_GROUPS,
  IVT_EDITOR_GROUP,
  IVT_VIEWER_GROUP,
} from '../../utils/authorization';
import './IvtLayout.css';

// IVT 自成产品面，没有 portal Sidebar — 这里提供 /ivt/* 树的顶部导航。
// NOTE: the Assets tab points at the v2 Overview (/ivt/assets, all origins). The
// old master-detail New Assets page is deprecated (M4): /ivt/new-assets now
// redirects to /ivt/assets, and it's gone from the nav. Its component code lingers
// (v2 reuses NewAssetCreateModal + some CSS) until a separate cleanup CR removes
// it. Solution Map no longer surfaces linked-asset chips — it is a strategic
// Area × UC view.
// Order is the one the team asked for and is NOT alphabetical or historical: it runs
// widest-scope-first — the leader's Dashboard, then Impact, then the pipeline, then the
// inventory that feeds them. Do not "tidy" it.
//
// ⚠️ Dashboard is shown to EVERY member even though its only operation is admin-only today,
// which is the opposite of how the Admin link below is treated. That asymmetry is deliberate:
// the Admin link leads to a page that will never be anything but admin-only, while this page
// holds two views and only the GM one is restricted — a non-admin will use the STO view from
// here once it exists. Until then a non-admin sees the page's no-access panel, which is why
// that panel keeps the window selector and the view switch mounted. Revisit hiding this tab
// only if the STO view is dropped.
const NAV_ITEMS = [
  { to: '/ivt/dashboard', label: 'Dashboard' },
  { to: '/ivt/impact', label: 'Impact' },
  { to: '/ivt/motions', label: 'Motion/Pipeline' },
  { to: '/ivt/assets', label: 'Assets' },
  { to: '/ivt/solution-map', label: 'Solution Map' },
];

// User Manual — a Quip doc, opened in a new tab. Right-aligned at the far end of
// the nav row (pushed there by margin-left:auto in CSS). External link, so a plain
// <a> with target=_blank (not a NavLink route).
const USER_MANUAL_URL = 'https://quip-amazon.com/jhWAAf0zq0aD';

export default function IvtLayout() {
  const { uid, ldap_groups } = useAuth();
  // Hidden rather than disabled for non-admins: the admin routes gate their READ too,
  // so there is nothing an editor could usefully see behind this link. The service is
  // still the authority — this only avoids offering a link that 403s.
  const isAdmin = hasIvtAdminAccess(ldap_groups);

  // "Any of the three", not "has ivthub": the backend admits editor-only and
  // admin-only members, and gating on a single group here would deny users the
  // service serves. See utils/authorization.ts for the group semantics.
  if (!hasIvtAccess(ldap_groups)) {
    return (
      <div className="ivt-layout__denied">
        <h2>IVT Hub 访问受限</h2>
        <p>
          当前账号 <strong>{uid}</strong> 不在 <code>{IVT_ACCESS_GROUPS.join(' / ')}</code>{' '}
          中的任一 LDAP 组中，无法访问 IVT Hub。
        </p>
        <p>
          请联系 IVT Hub 团队申请加入 <code>{IVT_VIEWER_GROUP}</code>（查看权限）；如果还需要
          新建 / 修改数据，请一并申请 <code>{IVT_EDITOR_GROUP}</code>（写权限）。
        </p>
      </div>
    );
  }

  return (
    <main className="ivt-layout__main">
      <ImpersonationBanner />
      <nav className="ivt-layout__nav">
        {NAV_ITEMS.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            className={({ isActive }) =>
              `ivt-layout__nav-link${isActive ? ' ivt-layout__nav-link--active' : ''}`
            }
          >
            {item.label}
          </NavLink>
        ))}
        {/* Right-hand group. Wrapped so the auto margin lives on the container: the
            admin entry renders conditionally, and hanging margin-left:auto off whichever
            item happens to be first would stop pushing the group right for non-admins. */}
        <div className="ivt-layout__nav-right">
          {isAdmin && (
            <NavLink
              to="/ivt/admin/configuration"
              className={({ isActive }) =>
                `ivt-layout__nav-link ivt-layout__nav-admin${
                  isActive ? ' ivt-layout__nav-link--active' : ''
                }`
              }
            >
              Admin
            </NavLink>
          )}
          {/* User Manual — external Quip doc → open in a new tab. */}
          <a
            className="ivt-layout__nav-link ivt-layout__nav-manual"
            href={USER_MANUAL_URL}
            target="_blank"
            rel="noopener noreferrer"
          >
            User Manual
          </a>
        </div>
      </nav>
      <Outlet />
    </main>
  );
}
